var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { C as CustomEvents, M as MediaStartedPlayingEvent } from "./events-DeC3n8dp.js";
const _CustomVideo = class _CustomVideo extends HTMLElement {
  constructor() {
    super();
    this.autoplay = this.dataset.autoplay === "true";
    this.loop = this.dataset.loop === "true";
    this.isPlaying = this.autoplay;
    this.isInitialized = false;
    this.ratioAuto = this.dataset.ratioAuto === "true";
  }
  connectedCallback() {
    this._abortController = new AbortController();
    document.addEventListener(CustomEvents.mediaStartedPlaying, this._handleMediaStarted.bind(this), { signal: this._abortController.signal });
    this.coverImage = this.querySelector("#cover-image");
    this.controls = this.querySelector("#controls");
    this.toggleWrap = this.querySelector("#button-controls");
    this.buttonPlay = this.querySelector(".vid-block-controls-play");
    this.buttonPause = this.querySelector(".vid-block-controls-pause");
    this._handlePointerMove = this._onPointerMove.bind(this);
    this._handlePointerLeave = this._handleLeave.bind(this);
    this._handleClick = () => {
      if (!this.isInitialized) return;
      this.toggleVideo();
    };
    if (this.controls) {
      this.controls.addEventListener("click", this._handleClick);
      this.controls.addEventListener("pointermove", this._handlePointerMove, { passive: true });
      this.controls.addEventListener("mouseleave", this._handlePointerLeave);
    }
    if (this.coverImage) {
      this.coverImage.addEventListener("click", this._handleClick);
    }
    const tpl = this.querySelector("template");
    if (tpl) {
      const root = this.closest("section") || this;
      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (entries[0].isIntersecting && !this.isInitialized) {
            this._initVideo(tpl);
            obs.disconnect();
          }
        },
        { threshold: 0.25 }
      );
      this._io.observe(root);
    }
  }
  disconnectedCallback() {
    var _a, _b, _c, _d, _e;
    (_a = this._abortController) == null ? void 0 : _a.abort();
    (_b = this.controls) == null ? void 0 : _b.removeEventListener("click", this._handleClick);
    (_c = this.controls) == null ? void 0 : _c.removeEventListener("pointermove", this._handlePointerMove);
    (_d = this.controls) == null ? void 0 : _d.removeEventListener("mouseleave", this._handlePointerLeave);
    if (this._scrollBound) window.removeEventListener("scroll", this._scrollBound);
    clearTimeout(this._leaveCleanup);
    clearTimeout(this._hideTimer);
    (_e = this._io) == null ? void 0 : _e.disconnect();
    this._msgHandler && window.removeEventListener("message", this._msgHandler);
  }
  _handleMediaStarted(event) {
    var _a;
    if (((_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.resource) === this) return;
    if (!this.isInitialized) return;
    if (!this.isPlaying) return;
    this._pause();
  }
  // ── Video lifecycle ──────────────────────────────────────
  _initVideo(tpl) {
    const el = tpl.content.querySelector("iframe, video");
    if (!el) return;
    this._media = el.cloneNode(true);
    tpl.insertAdjacentElement("afterend", this._media);
    tpl.remove();
    this.isInitialized = true;
    this.classList.add("loaded");
    const onReady = () => {
      var _a;
      if (this.isPlaying) {
        (_a = this.coverImage) == null ? void 0 : _a.classList.add("hidden");
      }
    };
    this._media.addEventListener(
      this._media.tagName === "IFRAME" ? "load" : "loadedmetadata",
      onReady,
      { once: true }
    );
    if (!this.loop) this._listenForEnd();
    if (this.ratioAuto) this._setAspectRatioAuto();
    if (this.autoplay) {
      this._play(true);
    }
  }
  toggleVideo() {
    this.isPlaying ? this._pause() : this._play();
  }
  _play(isInit = false) {
    var _a, _b;
    if (!this._media) return;
    if (this._media.tagName === "IFRAME") {
      const type = this._media.dataset.videoType;
      (_b = this._media.contentWindow) == null ? void 0 : _b.postMessage((_a = _CustomVideo.VIDEO_COMMANDS[type]) == null ? void 0 : _a.play, "*");
      this._setPlayUI(!isInit);
      if (!isInit) {
        setTimeout(() => {
          if (this.isPlaying) this.dispatchEvent(new MediaStartedPlayingEvent(this));
        }, 0);
      }
    } else {
      const playPromise = this._media.play();
      if (playPromise !== void 0) {
        playPromise.then(() => {
          this._setPlayUI(true);
          if (!isInit) this.dispatchEvent(new MediaStartedPlayingEvent(this));
        }).catch((err) => {
          this._setPauseUI();
        });
      } else {
        this._setPlayUI(true);
      }
    }
  }
  _pause() {
    var _a, _b;
    if (!this._media) return;
    if (this._media.tagName === "IFRAME") {
      const type = this._media.dataset.videoType;
      (_b = this._media.contentWindow) == null ? void 0 : _b.postMessage((_a = _CustomVideo.VIDEO_COMMANDS[type]) == null ? void 0 : _a.pause, "*");
    } else {
      this._media.pause();
    }
    this._setPauseUI();
  }
  _setPlayUI(hideCoverImmediately = true) {
    var _a;
    this.isPlaying = true;
    if (hideCoverImmediately) {
      (_a = this.coverImage) == null ? void 0 : _a.classList.add("hidden");
    }
    this.toggleButtonPlayPause();
    this._showControls();
    this._resetHideTimer();
  }
  _setPauseUI() {
    this.isPlaying = false;
    this.toggleButtonPlayPause();
    this._showControls();
    clearTimeout(this._hideTimer);
  }
  // ── Button / controls ────────────────────────────────────
  toggleButtonPlayPause() {
    var _a, _b;
    (_a = this.buttonPlay) == null ? void 0 : _a.classList.toggle("show", !this.isPlaying);
    (_b = this.buttonPause) == null ? void 0 : _b.classList.toggle("show", this.isPlaying);
  }
  _showControls() {
    var _a;
    (_a = this.toggleWrap) == null ? void 0 : _a.classList.remove("force-hide");
  }
  _hideControls() {
    var _a;
    if (!this.isPlaying) return;
    (_a = this.toggleWrap) == null ? void 0 : _a.classList.add("force-hide");
  }
  _resetHideTimer() {
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => this._hideControls(), 1e3);
  }
  // ── Cursor-follow (WAAPI stacking) ──────────────────────
  _updateTogglePosition() {
    if (!this.toggleWrap || this._lastX == null) return;
    const rect = (this.closest(".vid-block-content") || this.controls).getBoundingClientRect();
    this.toggleWrap.animate(
      { transform: `translate3d(${this._lastX - rect.left - rect.width / 2}px, ${this._lastY - rect.top - rect.height / 2}px, 0)` },
      { duration: 3200, fill: "forwards", easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
    );
  }
  _onPointerMove(e) {
    if (!this.toggleWrap) return;
    if (this.toggleWrap.classList.contains("force-hide")) this._showControls();
    clearTimeout(this._leaveCleanup);
    this._lastX = e.clientX;
    this._lastY = e.clientY;
    if (!this._scrollBound) {
      this._scrollBound = this._updateTogglePosition.bind(this);
      window.addEventListener("scroll", this._scrollBound, { passive: true });
    }
    this._updateTogglePosition();
    this._resetHideTimer();
  }
  _handleLeave() {
    if (this._scrollBound) {
      window.removeEventListener("scroll", this._scrollBound);
      this._scrollBound = null;
    }
    this._lastX = null;
    if (this.toggleWrap) {
      this.toggleWrap.animate(
        { transform: "translate3d(0, 0, 0)" },
        { duration: 2400, fill: "forwards", easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
      );
      this._leaveCleanup = setTimeout(() => {
        var _a;
        (_a = this.toggleWrap) == null ? void 0 : _a.getAnimations().forEach((a) => {
          if (a.playState === "finished") a.cancel();
        });
      }, 3e3);
    }
  }
  // ── Video end detection ──────────────────────────────────
  _listenForEnd() {
    if (!this._media) return;
    if (this._media.tagName === "IFRAME") {
      this._media.addEventListener("load", () => {
        var _a, _b;
        const type = this._media.dataset.videoType;
        (_b = this._media.contentWindow) == null ? void 0 : _b.postMessage((_a = _CustomVideo.VIDEO_COMMANDS[type]) == null ? void 0 : _a.ended, "*");
      }, { once: true });
      this._msgHandler = (event) => {
        var _a, _b;
        try {
          const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
          if (event.origin === "https://www.youtube.com" && data.event === "infoDelivery") {
            if (((_a = data.info) == null ? void 0 : _a.duration) > 0 && data.info.currentTime >= data.info.duration - 1) {
              this._onVideoEnd();
            }
            if (((_b = data.info) == null ? void 0 : _b.playerState) === 1) {
              if (!this.isPlaying) this._setPlayUI(true);
              this.dispatchEvent(new MediaStartedPlayingEvent(this));
            }
          }
          if (/^https?:\/\/player\.vimeo\.com/.test(event.origin)) {
            if (data.event === "ended") {
              this._onVideoEnd();
            }
            if (data.event === "play") {
              if (!this.isPlaying) this._setPlayUI(true);
              this.dispatchEvent(new MediaStartedPlayingEvent(this));
            }
          }
        } catch {
        }
      };
      window.addEventListener("message", this._msgHandler);
    } else {
      this._media.addEventListener("ended", () => this._onVideoEnd());
      this._media.addEventListener("play", () => {
        if (!this.isPlaying) this._setPlayUI(true);
        this.dispatchEvent(new MediaStartedPlayingEvent(this));
      });
      this._media.addEventListener("pause", () => {
        this.isPlaying = false;
        this.toggleButtonPlayPause();
      });
    }
  }
  _onVideoEnd() {
    this._setPauseUI();
  }
  // ── Aspect ratio for external video ─────────────────────
  _setAspectRatioAuto() {
    if (!this._media || this._media.tagName !== "IFRAME") return;
    this._media.style.cssText = "width:fit-content;height:auto";
    const w = this._media.offsetWidth;
    const h = this._media.offsetHeight;
    this._media.style.cssText = "width:100%;height:100%";
    if (!w || !h) return;
    const ratio = w / h;
    const block = this.closest(".vid-block");
    if (block) block.style.setProperty("--media-aspect-ratio", ratio);
  }
};
__publicField(_CustomVideo, "VIDEO_COMMANDS", {
  youtube: {
    play: '{"event":"command","func":"playVideo","args":""}',
    pause: '{"event":"command","func":"pauseVideo","args":""}',
    ended: '{"event": "listening"}'
  },
  vimeo: {
    play: '{"method":"play"}',
    pause: '{"method":"pause"}',
    ended: '{"method": "addEventListener", "value": "ended"}'
  }
});
let CustomVideo = _CustomVideo;
if (!customElements.get("custom-video")) {
  customElements.define("custom-video", CustomVideo);
}
